import React, { useRef, useEffect, useContext, Suspense, lazy, useState, useCallback, useMemo } from 'react'
import { Grid, Container, Divider, useMediaQuery } from '@material-ui/core'
import { useRouteMatch, Switch, Route, useHistory, useLocation, Redirect } from 'react-router-dom'
import { EventEmitterService, EventKey } from '../../services/EventEmitterService'
import UserProfileContext from '../../context/UserProfileContext'
import { SessionContextStore } from '../../context/SessionContext'
import ViolationsService from '../../services/ViolationsService'
import { BrowserView, isMobile, MobileView } from 'react-device-detect'
import { SessionService } from '../../services/SessionService'
import FlexView from 'react-flexview/lib'
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace'
import { lazyWithPreload } from '../../utilities/RLazyLoading'
import { BlueGrayBox } from '../Core/BlueGrayBox'
import GoogleOptimizeService, { EXPERIMENTS, VARIANTS } from '../../services/GoogleOptimizeService'
import { LoginProtectedRoute } from '../Core/Routes/LoginProtectedRoute'
import { CustomizeMotorcycle } from './CustomizeMotorcycle'
import { CustomizeOperators } from './CustomizeOperators'
import { CoveragePicker } from './CoveragePicker'
// import CoveragePickerNew from './CoveragePickerNew'
import { PlanSummary } from './Plans/PlanSummary'
import PlanSummaryMobile from './PlanSummaryMobile'
import { Issuance } from './Issuance'
import { reportGTM } from '../Core/Utilities/GTagManger'
import { OfferProtectedRoute } from '../../components/Core/Routes/OfferProtectedRoute'

const UserProfile = lazyWithPreload(() => import('../UserProfile/UserProfile').then(module => ({ default: module.UserProfile })))
const Congratulations = lazyWithPreload(() => import('./Congratulations').then(module => ({ default: module.Congratulations })))
const CreditCard = lazyWithPreload(() => import('../CreditCard/CreditCard').then(module => ({ default: module.CreditCard })))
const SkywatchProgressBar = lazy(() => import('../Core/SkywatchProgressBar').then(module => ({ default: module.SkywatchProgressBar })))
const PolicySummary = lazyWithPreload(() => import('./PolicySummary').then(module => ({ default: module.PolicySummary })))
const FlowLogin = lazyWithPreload(() => import('../Login/FlowLogin').then(module => ({ default: module.FlowLogin })))

const pages = [
  { path: 'profile', title: "Let's get started with your quote", subTitle: undefined },
  { path: 'motorcycles', title: "You're almost there…", subTitle: undefined },
  { path: 'operators', title: 'One more step', subTitle: undefined },
  { path: 'coverages', title: 'Insurance Plans', subTitle: 'Based on your annual mileage estimation' },
  { path: 'summary', title: 'Motorcycle Insurance Quote', subTitle: undefined },
]

export const MonthlyInsurance = () => {
  const sessionContext = useContext(SessionContextStore)
  const { path, url } = useRouteMatch()
  const history = useHistory()
  const location = useLocation()
  const [pageTitle, setPageTitle] = useState(undefined)
  const [pageSubTitle, setPageSubTitle] = useState(undefined)
  const isBookRoll = useMemo(() => {
    return sessionContext.isBookRoll()
  }, [sessionContext.user.source])

  useEffect(() => {
    setTimeout(() => {
      UserProfile.preload()
      Congratulations.preload()
      CreditCard.preload()
      PolicySummary.preload()
      FlowLogin.preload()
    }, 500)
  }, [])

  useEffect(() => {
    const currentPage = pages.find(p => `/insurance/${p.path}` == location.pathname)
    if (currentPage != undefined) {
      setPageTitle(currentPage.title)
      setPageSubTitle(currentPage.subTitle)
    }
  }, [window.location.pathname])

  // useEffect(() => {
  //   if (
  //     sessionContext.activeInsurance &&
  //     sessionContext.activeInsurance.insuranceId &&
  //     !SessionService.isBroker() &&
  //     location.pathname != `${url}/congratulations`
  //   ) {
  //     history.push('/policy-management')
  //   }
  // }, [sessionContext.user.email, sessionContext.activeInsurance, location.pathname])

  const isInitialMount = useRef(true)

  const handleFlow = useCallback(
    async eventKey => {
      let isVehiclePageOkRes
      let isOperatorsPageOkRes
      switch (eventKey) {
        case EventKey.FLOW_CUSTOMIZE_FINISH:
          if (!SessionService.isLoggedIn()) {
            if (GoogleOptimizeService.getVariant(EXPERIMENTS.FLOW_POP_VS_PAGE) == VARIANTS.Original) {
              reportGTM('', '', { event: 'login-popup-shown' })
              EventEmitterService.dispatch(EventKey.FLOW_NEED_LOGIN)
            } else if (GoogleOptimizeService.getVariant(EXPERIMENTS.FLOW_POP_VS_PAGE) == VARIANTS.Variant1) {
              reportGTM('', '', { event: 'passwordless-page-shown' })
              history.push(`${url}/email`)
            }
            break
          }
          if (!isBookRoll && ViolationsService.isUserProfileOK(sessionContext.user)) {
            handleFlow(EventKey.FLOW_PROFILE_FINISH)
          } else history.push(`${url}/profile`)
          break
        case EventKey.FLOW_PROFILE_FINISH:
          history.push(`${url}/motorcycles`)

          EventEmitterService.dispatch(EventKey.ShowLoader)
          isVehiclePageOkRes = await ViolationsService.isVehiclePageOk()
          EventEmitterService.dispatch(EventKey.DismissLoader)
          if (!isBookRoll && sessionContext.user.userVehicles.length > 0 && isVehiclePageOkRes.ok) {
            handleFlow(EventKey.FLOW_CUSTOMIZE_VEHICLES_FINISH)
          }

          break
        case EventKey.FLOW_CUSTOMIZE_VEHICLES_FINISH:
          history.push(`${url}/operators`)
          EventEmitterService.dispatch(EventKey.ShowLoader)
          isOperatorsPageOkRes = await ViolationsService.isOperatorsPageOk()
          EventEmitterService.dispatch(EventKey.DismissLoader)
          if (
            !isBookRoll &&
            sessionContext.user.userInsuredPersons.length > 0 &&
            !sessionContext.doOrphanVehiclesExist() &&
            isOperatorsPageOkRes.ok
          ) {
            handleFlow(EventKey.FLOW_CUSTOMIZE_OPERATORS_FINISH)
          }
          break
        case EventKey.FLOW_CUSTOMIZE_OPERATORS_FINISH:
          if (sessionContext.user.userVehicles.every(x => x.new_vehicle_req_details == '')) {
            if (!isMobile) history.push(`${url}/coverages`)
            else history.push(`${url}/summary`)
          }
          break
        case EventKey.FLOW_COVERAGE_PLAN_PICKED:
          // history.push('/thankyou')
          history.push(`${url}/summary`)
          break
        case EventKey.FLOW_ISSUANCE_FINISHED:
          history.push(`${url}/credit-card`)
          break
        case EventKey.FLOW_SUMMARY_FINISH:
          history.push(`${url}/issuance`)
          break
      }
    },
    [EventEmitterService, EventKey, history, isMobile, sessionContext.doOrphanVehiclesExist, sessionContext.user.userInsure, isBookRoll]
  )
  useEffect(() => {
    if (
      sessionContext.activeInsurance &&
      sessionContext.activeInsurance.insuranceId &&
      !SessionService.isBroker() &&
      location.pathname != `${url}/congratulations`
    ) {
      history.push('/policy-management')
      return
    }
    const id2 = EventEmitterService.subscribe(EventKey.FLOW_GO_BACK, () => {
      switch (location.pathname) {
        case `${url}/credit-card`:
          history.replace(`${url}/issuance`)
          break
        case `${url}/motorcycles`:
          history.replace(`${url}/profile`)
          break
        case `${url}/operators`:
          history.replace(`${url}/motorcycles`)
          break
        case `${url}/coverages`:
          history.replace(`${url}/operators`)
          break
        case `${url}/summary`:
          // if (SessionService.isBroker() && !FirebaseService.getBoolean(FirebaseConfigKey.PAYMENT_REQUIRED_FOR_BROKER)){
          //     history.replace(url + '/profile')
          // }
          // else {
          //     history.replace(url + '/credit-card')
          // }
          if (newDesign) history.replace(`${url}/operators`)
          else history.replace(`${url}/coverages`)
          break
        case `${url}/issuance`:
          history.replace(`${url}/summary`)
          break
        default:
          history.goBack()
          break
      }
    })

    const id1 = EventEmitterService.subscribe(EventKey.FLOW_CUSTOMIZE_FINISH, () => {
      handleFlow(EventKey.FLOW_CUSTOMIZE_FINISH)
    })

    const id3 = EventEmitterService.subscribe(EventKey.FLOW_PROFILE_FINISH, () => {
      handleFlow(EventKey.FLOW_PROFILE_FINISH)
    })

    const id4 = EventEmitterService.subscribe(EventKey.FLOW_CC_FINISH, () => {
      handleFlow(EventKey.FLOW_CC_FINISH)
    })

    const id5 = EventEmitterService.subscribe(EventKey.FLOW_INSURANCE_PURCHASED_SUCCESS, resp => {
      history.push(`${url}/congratulations`, { resp })
      sessionContext.loadActiveInsurance()
    })

    const id6 = EventEmitterService.subscribe(EventKey.FLOW_SUMMARY_FINISH, resp => {
      handleFlow(EventKey.FLOW_SUMMARY_FINISH)
    })

    const id7 = EventEmitterService.subscribe(EventKey.FLOW_CUSTOMIZE_VEHICLES_FINISH, () => {
      handleFlow(EventKey.FLOW_CUSTOMIZE_VEHICLES_FINISH)
    })

    const id8 = EventEmitterService.subscribe(EventKey.FLOW_CUSTOMIZE_OPERATORS_FINISH, () => {
      handleFlow(EventKey.FLOW_CUSTOMIZE_OPERATORS_FINISH)
    })

    const id9 = EventEmitterService.subscribe(EventKey.FLOW_COVERAGE_PLAN_PICKED, () => {
      handleFlow(EventKey.FLOW_COVERAGE_PLAN_PICKED)
    })

    const id10 = EventEmitterService.subscribe(EventKey.FLOW_ISSUANCE_FINISHED, () => {
      handleFlow(EventKey.FLOW_ISSUANCE_FINISHED)
    })
    if (
      isInitialMount.current &&
      sessionContext.user.userVehicles.length > 0 &&
      ViolationsService.isVehiclePageOk() &&
      ViolationsService.isUserProfileOK(sessionContext.user) &&
      !isBookRoll &&
      (location.pathname == '/insurance/profile' ||
        location.pathname == '/insurance/motorcycles' ||
        location.pathname == '/insurance/operators' ||
        location.pathname == '/insurance/coverages')
    ) {
      isInitialMount.current = false
      handleFlow(EventKey.FLOW_PROFILE_FINISH)
    } else if (
      isInitialMount.current &&
      ViolationsService.isUserProfileOK(sessionContext.user) &&
      (location.pathname == '/insurance/profile' ||
        location.pathname == '/insurance/motorcycles' ||
        location.pathname == '/insurance/operators' ||
        location.pathname == '/insurance/coverages')
    ) {
      isInitialMount.current = false
      handleFlow(EventKey.FLOW_CUSTOMIZE_FINISH)
    }

    return function cleanup() {
      EventEmitterService.unsubscribe(EventKey.FLOW_CUSTOMIZE_FINISH, id1)
      EventEmitterService.unsubscribe(EventKey.FLOW_GO_BACK, id2)
      EventEmitterService.unsubscribe(EventKey.FLOW_PROFILE_FINISH, id3)
      EventEmitterService.unsubscribe(EventKey.FLOW_CC_FINISH, id4)
      EventEmitterService.unsubscribe(EventKey.FLOW_INSURANCE_PURCHASED_SUCCESS, id5)
      EventEmitterService.unsubscribe(EventKey.FLOW_SUMMARY_FINISH, id6)
      EventEmitterService.unsubscribe(EventKey.FLOW_CUSTOMIZE_VEHICLES_FINISH, id7)
      EventEmitterService.unsubscribe(EventKey.FLOW_CUSTOMIZE_OPERATORS_FINISH, id8)
      EventEmitterService.unsubscribe(EventKey.FLOW_COVERAGE_PLAN_PICKED, id9)
      EventEmitterService.unsubscribe(EventKey.FLOW_ISSUANCE_FINISHED, id10)
    }
  }, [sessionContext.user, location, sessionContext.activeInsurance])
  const flowGoBack = () => EventEmitterService.dispatch(EventKey.FLOW_GO_BACK)

  const newDesign = isMobile && location.pathname == `/insurance/summary` && process.env.REACT_APP_NEW_DESIGN === 'true'
  return (
    <Container
      id="container"
      style={{
        backgroundColor: newDesign ? '#faf8f6' : 'transparent',
        padding: 0,
        ...(newDesign && { height: 'calc(100% - 3.7rem)', overflow: 'hidden', display: 'flex', flexDirection: 'column', flexShrink: 0 }),
      }}
    >
      <BrowserView>
        <FlexView vAlignContent={'center'} hAlignContent={'center'} className="space-between margin-top-basic margin-bottom-basic">
          <FlexView vAlignContent={'bottom'} hAlignContent={'center'} className="space-between margin-top-basic margin-bottom-basic">
            <FlexView vAlignContent="center" hAlignContent="center" column>
              <div className="text-promotion black bold">{pageTitle}</div>
              {!!pageSubTitle && <div className="text-body backbuttongray bold textcenter margin-top-basic">{pageSubTitle}</div>}
            </FlexView>
            {SessionService.isBroker() && (
              <div className="margin-top-basic text-tiny margin-left-6">
                <BlueGrayBox>{sessionContext.user.email}</BlueGrayBox>
              </div>
            )}
          </FlexView>
        </FlexView>
      </BrowserView>
      <MobileView>
        <FlexView className="margin-top-small space-between" vAlignContent={'center'}>
          <FlexView vAlignContent={'center'}>
            {location.pathname != '/thankyou' && location.pathname != '/insurance/profile' && (
              <KeyboardBackspaceIcon onClick={flowGoBack} className="black" />
            )}
            <FlexView column vAlignContent={'center'}>
              <div className="text-title bold margin-left-basic">{pageTitle}</div>
              {!!pageSubTitle && <div className="text-small backbuttongray textcenter">{pageSubTitle}</div>}
            </FlexView>
            {SessionService.isBroker() && (
              <div className="text-tiny margin-left-6">
                <BlueGrayBox>{sessionContext.user.email}</BlueGrayBox>
              </div>
            )}
          </FlexView>
        </FlexView>
        <Divider />
      </MobileView>
      <Suspense fallback={<div />}>
        <SkywatchProgressBar />
      </Suspense>
      <Grid
        container
        style={{
          ...(newDesign && { height: '100%', overflow: 'hidden' }),
        }}
      >
        <Grid
          item
          xs
          style={{
            ...(newDesign && { height: '100%', overflow: 'hidden' }),
          }}
        >
          {/* Incase path == /insurance , redirects to annual or monthly */}
          <Route exact path={url}>
            <Redirect exact to={`${url}/profile`} />
          </Route>

          {/* <Route path={`${path}/email`}>
                  <Suspense fallback={<div />}>
                    <PasswordlessEmailForm />
                  </Suspense>
                </Route> */}
          <Route path={`${path}/profile`}>
            <UserProfileContext>
              <Suspense fallback={<div />}>
                <UserProfile />
              </Suspense>
            </UserProfileContext>
          </Route>

          <LoginProtectedRoute redirectTo="/welcome" path={`${path}/coverages`}>
            <CoveragePicker />
          </LoginProtectedRoute>

          <LoginProtectedRoute redirectTo="/welcome" path={`${path}/motorcycles`}>
            <CustomizeMotorcycle />
          </LoginProtectedRoute>

          <LoginProtectedRoute redirectTo="/welcome" path={`${path}/operators`}>
            <CustomizeOperators />
          </LoginProtectedRoute>

          <LoginProtectedRoute redirectTo="/welcome" path={`${path}/issuance`}>
            <Issuance />
          </LoginProtectedRoute>

          <OfferProtectedRoute path={`${path}/credit-card`}>
            <Suspense fallback={<div />}>
              <CreditCard />
            </Suspense>
          </OfferProtectedRoute>

          <LoginProtectedRoute path={`${path}/summary`}>
            <Suspense fallback={<div />}>{newDesign ? <PlanSummaryMobile /> : <PlanSummary />}</Suspense>
          </LoginProtectedRoute>

          <Suspense fallback={<div />}>
            <FlowLogin finishedEvent={EventKey.FLOW_CUSTOMIZE_FINISH} />
          </Suspense>
        </Grid>
        <Grid item>
          {!isMobile &&
            location.pathname != '/insurance/congratulations' &&
            location.pathname != '/insurance/profile' &&
            location.pathname != '/insurance/motorcycles' &&
            location.pathname != '/insurance/operators' &&
            location.pathname != '/insurance/coverages' && (
              <Suspense fallback={<div />}>
                <PolicySummary />
              </Suspense>
            )}
        </Grid>
      </Grid>
      <Route path={`${path}/congratulations`}>
        <Suspense fallback={<div />}>
          <Congratulations />
        </Suspense>
      </Route>
    </Container>
  )
}
